import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Container,
    Stack,
    Divider,
    Link as MuiLink,
    Chip,
    Collapse,
    IconButton,
    GlobalStyles,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
    CheckCircle as CheckIcon,
    Bolt as BoltIcon,
    AutoAwesome as AutoIcon,
    Insights as InsightsIcon,
    Reply as ReplyIcon,
    ArrowForward as ArrowForwardIcon,
    Title as TitleIcon,
    Dashboard as DashboardIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
    PlayCircle as PlayIcon,
    Tune as TuneIcon,
    BarChart as BarChartIcon,
    Group as GroupIcon,
} from '@mui/icons-material';
import PricingSection from './PricingSection';
import NewsletterSection from './NewsletterSection';
import { featuresApi, blogApi } from '../../services/api';

/* ─────────────────────────────────────────────────────────────────────────────
   HERO DASHBOARD CAROUSEL — 3D Perspective Showcase
   Modern SaaS-style with perspective tilt, animated border, depth layers
───────────────────────────────────────────────────────────────────────────── */
const HERO_SCREENSHOTS = [
    { src: '/assets/screenshots/overview.png',  label: 'Analytics Overview',  color: '#00d2ff', icon: '📊' },
    { src: '/assets/screenshots/instagram.png', label: 'Instagram Insights',  color: '#E1306C', icon: '📸' },
    { src: '/assets/screenshots/youtube.png',   label: 'YouTube Studio',      color: '#FF0000', icon: '▶️' },
    { src: '/assets/screenshots/facebook.png',  label: 'Facebook Analytics',  color: '#1877F2', icon: '👥' },
];

const LIVE_METRICS = [
    { value: '+847',  label: 'New followers',   color: '#10d98a' },
    { value: '6.58%', label: 'Engagement',      color: '#f59e0b' },
    { value: '2.4K',  label: 'Likes today',     color: '#f43f5e' },
    { value: '128K',  label: 'Impressions',      color: '#6366f1' },
];

// Tab positions for cursor — where each platform tab sits in the screenshot
// The dashboard sidebar crops ~15% on left with objectFit:cover, tabs are in the header row
const TAB_CURSOR_TARGETS = [
    { x: '10%', y: '16%' },    // Overview tab
    { x: '30%', y: '23%' },    // Instagram tab (2nd in tab row)
    { x: '30%', y: '20%' },    // YouTube tab (3rd in tab row)
    { x: '36%', y: '15%' },    // Facebook tab (4th in tab row)
];

const HeroDashboardCarousel = () => {
    const [active, setActive] = useState(0);
    const [cursorPhase, setCursorPhase] = useState('idle'); // 'idle' | 'moving' | 'clicking' | 'done'
    const [cursorPos, setCursorPos] = useState(TAB_CURSOR_TARGETS[0]);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (isHovered) return;

        // Cycle: idle 2s → move cursor 0.6s → click 0.3s → switch tab → idle ...
        const IDLE_MS = 2200;
        const MOVE_MS = 700;
        const CLICK_MS = 300;

        let timeout;
        const nextIdx = (active + 1) % HERO_SCREENSHOTS.length;

        // Phase 1: idle — screenshot is visible
        setCursorPhase('idle');
        timeout = setTimeout(() => {
            // Phase 2: move cursor to next tab
            setCursorPhase('moving');
            setCursorPos(TAB_CURSOR_TARGETS[nextIdx]);

            timeout = setTimeout(() => {
                // Phase 3: click animation
                setCursorPhase('clicking');

                timeout = setTimeout(() => {
                    // Phase 4: switch the tab
                    setActive(nextIdx);
                    setCursorPhase('done');
                }, CLICK_MS);
            }, MOVE_MS);
        }, IDLE_MS);

        return () => clearTimeout(timeout);
    }, [active, isHovered]);

    const current = HERO_SCREENSHOTS[active];
    const next = HERO_SCREENSHOTS[(active + 1) % HERO_SCREENSHOTS.length];

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                position: 'relative',
                perspective: '1200px',
                '@keyframes borderRotate': {
                    '0%':   { '--angle': '0deg' },
                    '100%': { '--angle': '360deg' },
                },
                '@keyframes floatUp': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%':     { transform: 'translateY(-8px)' },
                },
                '@keyframes shimmer': {
                    '0%':   { backgroundPosition: '-200% 0' },
                    '100%': { backgroundPosition: '200% 0' },
                },
                '@keyframes pulseGlow': {
                    '0%, 100%': { opacity: 0.6 },
                    '50%':     { opacity: 1 },
                },
                '@keyframes clickRipple': {
                    '0%':   { transform: 'scale(0)', opacity: 0.6 },
                    '100%': { transform: 'scale(3)', opacity: 0 },
                },
                '@keyframes cursorBounce': {
                    '0%':   { transform: 'scale(1)' },
                    '50%':  { transform: 'scale(0.75)' },
                    '100%': { transform: 'scale(1)' },
                },
            }}
        >
            {/* Deep ambient glow */}
            <Box sx={{
                position: 'absolute', top: '-10%', left: '-5%', right: '-5%', bottom: '-10%',
                background: `
                    radial-gradient(ellipse 60% 50% at 50% 40%, ${current.color}18 0%, transparent 70%),
                    radial-gradient(ellipse 40% 60% at 30% 70%, ${next.color}10 0%, transparent 60%)
                `,
                filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0,
                transition: 'background 1.2s ease',
            }} />

            {/* Stacked depth cards */}
            {[2, 1].map(depth => (
                <Box key={depth} sx={{
                    position: 'absolute',
                    top: depth * 12, left: depth * 8, right: depth * 8, bottom: -(depth * 4),
                    borderRadius: '20px',
                    bgcolor: `rgba(18,20,36,${0.3 + depth * 0.15})`,
                    border: '1px solid rgba(255,255,255,0.04)',
                    transform: `rotateY(-3deg) rotateX(2deg) translateZ(-${depth * 30}px)`,
                    transition: 'all 0.6s ease',
                    zIndex: 0,
                }} />
            ))}

            {/* Main card with 3D tilt */}
            <Box sx={{
                position: 'relative', zIndex: 2,
                transform: isHovered
                    ? 'rotateY(-2deg) rotateX(1deg) scale(1.02)'
                    : 'rotateY(-4deg) rotateX(2deg)',
                transition: 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
                transformStyle: 'preserve-3d',
            }}>
                {/* Animated gradient border */}
                <Box sx={{
                    position: 'absolute', inset: -1, borderRadius: '21px', zIndex: -1,
                    background: `conic-gradient(from var(--angle, 0deg), ${current.color}, #6366f1, ${current.color}40, ${next.color}, ${current.color})`,
                    animation: 'borderRotate 6s linear infinite',
                    opacity: 0.5,
                    filter: 'blur(2px)',
                    transition: 'background 0.8s ease, opacity 0.3s',
                    '@property --angle': { syntax: '"<angle>"', initialValue: '0deg', inherits: false },
                }} />

                {/* Card body */}
                <Box sx={{
                    borderRadius: '20px',
                    background: 'linear-gradient(145deg, rgba(16,18,34,0.97) 0%, rgba(8,10,24,0.99) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    overflow: 'hidden',
                    boxShadow: `
                        0 50px 100px -20px rgba(0,0,0,0.8),
                        0 0 0 1px rgba(255,255,255,0.05) inset,
                        0 0 80px -30px ${current.color}25
                    `,
                    transition: 'box-shadow 0.6s ease',
                }}>
                    {/* Top bar — minimal chrome */}
                    <Box sx={{
                        px: 2.5, py: 1.2,
                        display: 'flex', alignItems: 'center', gap: 2,
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        background: 'rgba(255,255,255,0.02)',
                    }}>
                        <Box sx={{ display: 'flex', gap: 0.7 }}>
                            {['#ff5f57', '#ffbd2e', '#28c840'].map(c => (
                                <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, opacity: 0.85 }} />
                            ))}
                        </Box>
                        <Box sx={{
                            flex: 1, height: 28, borderRadius: '8px',
                            bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
                            display: 'flex', alignItems: 'center', px: 1.5, gap: 1,
                        }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10d98a', boxShadow: '0 0 4px #10d98a' }} />
                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500, letterSpacing: '0.02em' }}>
                                socialixer.com/dashboard
                            </Typography>
                        </Box>
                        {/* Platform pill tabs in chrome */}
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
                            {HERO_SCREENSHOTS.map((ss, i) => (
                                <Box key={i} onClick={() => setActive(i)} sx={{
                                    px: 1.2, py: 0.3, borderRadius: '6px', cursor: 'pointer',
                                    bgcolor: i === active ? `${ss.color}15` : 'transparent',
                                    border: `1px solid ${i === active ? `${ss.color}30` : 'transparent'}`,
                                    transition: 'all 0.3s',
                                    '&:hover': { bgcolor: `${ss.color}10` },
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: i === active ? ss.color : 'rgba(255,255,255,0.25)' }}>
                                        {ss.icon}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Screenshot area */}
                    <Box sx={{
                        position: 'relative',
                        height: { xs: 240, sm: 320, md: 400, lg: 460 },
                        overflow: 'hidden',
                    }}>
                        {HERO_SCREENSHOTS.map((ss, i) => (
                            <Box key={ss.src} component="img" src={ss.src} alt={ss.label}
                                sx={{
                                    position: 'absolute', top: 0, left: 0,
                                    width: '100%', height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'top center',
                                    transition: 'opacity 0.9s cubic-bezier(0.4, 0, 0.2, 1), transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                                    opacity: i === active ? 1 : 0,
                                    transform: i === active ? 'scale(1)' : 'scale(1.05)',
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ))}

                        {/* Animated cursor */}
                        <Box sx={{
                            position: 'absolute',
                            left: cursorPos.x,
                            top: cursorPos.y,
                            zIndex: 20,
                            pointerEvents: 'none',
                            transition: cursorPhase === 'moving'
                                ? 'left 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), top 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)'
                                : 'none',
                            display: { xs: 'none', md: 'block' },
                        }}>
                            {/* Cursor SVG */}
                            <Box
                                component="svg"
                                viewBox="0 0 24 24"
                                sx={{
                                    width: 22, height: 22,
                                    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.7))',
                                    animation: cursorPhase === 'clicking' ? 'cursorBounce 0.3s ease' : 'none',
                                }}
                            >
                                <path
                                    d="M5 3l14 8.5-6.5 1.5-3.5 6z"
                                    fill="#fff"
                                    stroke="#000"
                                    strokeWidth="1.5"
                                    strokeLinejoin="round"
                                />
                            </Box>

                            {/* Click ripple */}
                            {cursorPhase === 'clicking' && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: 4, left: 4,
                                    width: 12, height: 12,
                                    borderRadius: '50%',
                                    bgcolor: `${HERO_SCREENSHOTS[(active + 1) % HERO_SCREENSHOTS.length].color}60`,
                                    animation: 'clickRipple 0.4s ease-out forwards',
                                }} />
                            )}
                        </Box>

                        {/* Subtle vignette */}
                        <Box sx={{
                            position: 'absolute', inset: 0, pointerEvents: 'none',
                            background: `
                                linear-gradient(to top, rgba(8,10,24,0.9) 0%, transparent 30%),
                                linear-gradient(to bottom, rgba(8,10,24,0.3) 0%, transparent 15%),
                                radial-gradient(ellipse at center, transparent 50%, rgba(8,10,24,0.4) 100%)
                            `,
                        }} />

                        {/* Bottom info strip */}
                        <Box sx={{
                            position: 'absolute', bottom: 0, left: 0, right: 0,
                            px: 2.5, py: 1.5,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ position: 'relative', width: 10, height: 10 }}>
                                    <Box sx={{
                                        position: 'absolute', inset: 0, borderRadius: '50%',
                                        bgcolor: current.color, opacity: 0.4,
                                        animation: 'pulseGlow 2s ease-in-out infinite',
                                        transform: 'scale(2)',
                                    }} />
                                    <Box sx={{
                                        position: 'absolute', inset: 0, borderRadius: '50%',
                                        bgcolor: current.color,
                                        boxShadow: `0 0 6px ${current.color}`,
                                    }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.01em' }}>
                                    {current.label}
                                </Typography>
                                <Chip
                                    label="LIVE"
                                    size="small"
                                    sx={{
                                        height: 18, fontSize: '0.55rem', fontWeight: 900,
                                        bgcolor: `${current.color}15`, color: current.color,
                                        border: `1px solid ${current.color}30`,
                                        letterSpacing: '0.08em',
                                    }}
                                />
                            </Box>

                            {/* Progress indicators */}
                            <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
                                {HERO_SCREENSHOTS.map((ss, i) => (
                                    <Box key={i} onClick={() => setActive(i)} sx={{
                                        height: 3, borderRadius: 99, cursor: 'pointer',
                                        width: i === active ? 32 : 6,
                                        bgcolor: i === active ? ss.color : 'rgba(255,255,255,0.12)',
                                        boxShadow: i === active ? `0 0 8px ${ss.color}50` : 'none',
                                        transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        '&:hover': { bgcolor: i === active ? ss.color : 'rgba(255,255,255,0.25)' },
                                    }}>
                                        {i === active && (
                                            <Box sx={{
                                                position: 'absolute', inset: 0,
                                                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                                                backgroundSize: '200% 100%',
                                                animation: 'shimmer 2s ease-in-out infinite',
                                            }} />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Floating metric cards along the right edge */}
            <Box sx={{
                position: 'absolute',
                top: '12%', right: { xs: '-4%', md: '-8%' },
                display: { xs: 'none', sm: 'flex' },
                flexDirection: 'column', gap: 1.5,
                zIndex: 5,
            }}>
                {LIVE_METRICS.map((m, i) => (
                    <Box key={m.label} sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.8, borderRadius: '10px',
                        bgcolor: 'rgba(8,12,28,0.9)',
                        border: `1px solid ${m.color}20`,
                        backdropFilter: 'blur(16px)',
                        boxShadow: `0 8px 20px rgba(0,0,0,0.4), 0 0 0 1px ${m.color}10 inset`,
                        animation: 'floatUp 5s ease-in-out infinite',
                        animationDelay: `${i * 0.7}s`,
                        transform: 'translateX(0)',
                        transition: 'all 0.3s ease',
                        cursor: 'default',
                        '&:hover': {
                            transform: 'translateX(-4px) scale(1.05)',
                            boxShadow: `0 12px 28px rgba(0,0,0,0.5), 0 0 20px ${m.color}20`,
                            borderColor: `${m.color}40`,
                        },
                    }}>
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '8px',
                            bgcolor: `${m.color}15`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: m.color, boxShadow: `0 0 8px ${m.color}` }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#fff', lineHeight: 1.1 }}>{m.value}</Typography>
                            <Typography sx={{ fontSize: '0.58rem', color: m.color, fontWeight: 700, lineHeight: 1, letterSpacing: '0.02em' }}>{m.label}</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   REUSABLE ZIGZAG SECTION
   Text + image always side-by-side. `reverse` flips which side image goes on.
───────────────────────────────────────────────────────────────────────────── */
const ZigzagSection = ({ tag, tagColor = '#00d2ff', icon, title, description, points, imageSrc, reverse = false }) => {
    const accent = tagColor;
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
            alignItems: 'center',
            gap: { xs: 4, md: 7 },
            mb: { xs: 6, md: 8 },
        }}>
            {/* TEXT */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: 2,
                        bgcolor: `${accent}15`, border: `1px solid ${accent}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        {icon}
                    </Box>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: accent, letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        {tag}
                    </Typography>
                </Box>

                <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3.2rem' }, lineHeight: 1.1, letterSpacing: '-0.03em', mb: 2.5 }}>
                    {title}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: '1.05rem', mb: 3, maxWidth: 480 }}>
                    {description}
                </Typography>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 3 }} />

                <Stack spacing={1.5} sx={{ mb: 3.5 }}>
                    {points.map((pt, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckIcon sx={{ color: accent, fontSize: 18, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{pt}</Typography>
                        </Box>
                    ))}
                </Stack>

                <Button component={Link} to="/app" endIcon={<ArrowForwardIcon />} sx={{ color: accent, fontWeight: 800, fontSize: '1rem', textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.8 } }}>
                    Learn how it works
                </Button>
            </Box>

            {/* IMAGE */}
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <Box sx={{ position: 'absolute', inset: '-30%', background: `radial-gradient(circle, ${accent}18 0%, transparent 70%)`, zIndex: 0, pointerEvents: 'none' }} />
                <Box sx={{
                    position: 'relative', zIndex: 1,
                    borderRadius: '14px', overflow: 'hidden',
                    border: `1.5px solid ${accent}25`,
                    background: `linear-gradient(145deg, ${accent}08 0%, rgba(15,23,42,0.8) 100%)`,
                    backdropFilter: 'blur(16px)',
                    boxShadow: `0 30px 80px rgba(0,0,0,0.5), 0 0 60px -20px ${accent}15`,
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 30px 80px rgba(0,0,0,0.5), 0 0 80px -20px ${accent}30`, borderColor: `${accent}45` },
                }}>
                    {/* Ambient glow */}
                    <Box sx={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: `radial-gradient(circle, ${accent}18 0%, transparent 70%)`, pointerEvents: 'none' }} />
                    <Box component="img" src={imageSrc} alt={tag} sx={{ width: '100%', display: 'block', position: 'relative', zIndex: 1 }}
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.minHeight = '320px'; }}
                    />
                    {/* Bottom accent bar */}
                    <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}40)`, position: 'relative', zIndex: 1 }} />
                </Box>
            </Box>
        </Box>
    );
};

const DEMO_LEADS = [
    {
        name: 'Priya Sharma',
        initials: 'PS',
        avatarBg: '#e1306c',          // Instagram pink
        platform: 'Instagram',
        platformColor: '#e1306c',
        comment: '"This is exactly what I needed for my startup 🔥"',
        status: 'Hot Lead',
        statusColor: '#ff4d4d',
        statusBg: 'rgba(255,77,77,0.12)',
        time: '2 min ago',
        intent: 'High Intent',
    },
    {
        name: 'Arjun Mehta',
        initials: 'AM',
        avatarBg: '#ff0000',          // YouTube red
        platform: 'YouTube',
        platformColor: '#ff0000',
        comment: '"How do I sign up for the early access? Looks insane!"',
        status: 'Interested',
        statusColor: '#00d2ff',
        statusBg: 'rgba(0,210,255,0.12)',
        time: '8 min ago',
        intent: 'Medium Intent',
    },
    {
        name: 'Neha Patel',
        initials: 'NP',
        avatarBg: '#1877f2',          // Facebook blue
        platform: 'Facebook',
        platformColor: '#1877f2',
        comment: '"We need this for our agency. What\'s the pricing?"',
        status: 'Hot Lead',
        statusColor: '#ff4d4d',
        statusBg: 'rgba(255,77,77,0.12)',
        time: '15 min ago',
        intent: 'High Intent',
    },
];

const DemoCrmPreview = () => (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Header bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.6rem' }}>
                AI-DETECTED LEADS · 3 NEW TODAY
            </Typography>
            <Box sx={{ px: 1.5, py: 0.3, borderRadius: '100px', bgcolor: 'rgba(255,77,77,0.12)', border: '1px solid rgba(255,77,77,0.25)' }}>
                <Typography variant="caption" sx={{ color: '#ff4d4d', fontWeight: 800, fontSize: '0.6rem' }}>● LIVE</Typography>
            </Box>
        </Box>

        {/* Lead Cards */}
        {DEMO_LEADS.map((lead, i) => (
            <Box key={i} sx={{
                p: 1.5, borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.06)',
                bgcolor: 'rgba(255,255,255,0.02)',
                display: 'flex', gap: 1.5, alignItems: 'flex-start',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)' },
            }}>
                {/* Avatar */}
                <Box sx={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    bgcolor: lead.avatarBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '0.75rem', color: 'white',
                    border: `2px solid ${lead.avatarBg}60`,
                }}>
                    {lead.initials}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'white', fontSize: '0.78rem' }}>
                            {lead.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: lead.platformColor, fontSize: '0.65rem', fontWeight: 700 }}>
                            via {lead.platform}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem', ml: 'auto', flexShrink: 0 }}>
                            {lead.time}
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', display: 'block', mb: 0.8, fontStyle: 'italic' }}>
                        {lead.comment}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ px: 1, py: 0.2, borderRadius: '100px', bgcolor: lead.statusBg, border: `1px solid ${lead.statusColor}40` }}>
                            <Typography variant="caption" sx={{ color: lead.statusColor, fontWeight: 800, fontSize: '0.58rem' }}>
                                {lead.status}
                            </Typography>
                        </Box>
                        <Box sx={{ px: 1, py: 0.2, borderRadius: '100px', bgcolor: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)' }}>
                            <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 700, fontSize: '0.58rem' }}>
                                ✦ AI: {lead.intent}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        ))}
    </Box>
);

/* ─────────────────────────────────────────────────────────────────────────────
   INLINE DEMO COMPONENT
   Tab switcher showing different feature previews
───────────────────────────────────────────────────────────────────────────── */
const DemoSection = () => {
    const [activeTab, setActiveTab] = useState(0);
    const tabScrollRef = useRef(null);
    const [showRightHint, setShowRightHint] = useState(true);
    const [showLeftHint, setShowLeftHint] = useState(false);

    const handleTabScroll = (e) => {
        const el = e.target;
        const atStart = el.scrollLeft <= 8;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
        setShowLeftHint(!atStart);
        setShowRightHint(!atEnd);
    };

    const scrollTabsRight = () => {
        if (tabScrollRef.current) {
            tabScrollRef.current.scrollBy({ left: 140, behavior: 'smooth' });
        }
    };

    const scrollTabsLeft = () => {
        if (tabScrollRef.current) {
            tabScrollRef.current.scrollBy({ left: -140, behavior: 'smooth' });
        }
    };

    const tabs = [
        {
            label: 'Demo Walkthrough',
            icon: <PlayIcon sx={{ fontSize: 16 }} />,
            desc: 'Watch a full tour of the dashboard, cross-platform analytics, and AI content generation tools.',
            image: '/assets/demo.webp',
            accent: '#ff0000',
            badge: 'VIDEO',
        },
        {
            label: 'Lead CRM',
            icon: <GroupIcon sx={{ fontSize: 16 }} />,
            desc: 'View and manage all AI-detected leads from every platform in one unified dashboard.',
            image: '/assets/crm_preview.png',
            accent: '#00d2ff',
            badge: 'LIVE',
        },
        {
            label: 'AI Content',
            icon: <AutoIcon sx={{ fontSize: 16 }} />,
            desc: 'One click generates SEO-optimised titles, descriptions, hooks and hashtags powered by Gemini AI.',
            image: '/assets/hero_mockup.png',
            accent: '#8b5cf6',
            badge: 'AI',
        },
        {
            label: 'Analytics',
            icon: <BarChartIcon sx={{ fontSize: 16 }} />,
            desc: 'Cross-platform performance analytics — compare reach, engagement and conversions side-by-side.',
            image: '/assets/screenshots/overview.png',
            accent: '#10d98a',
            badge: 'INSIGHTS',
        },
    ];

    const t = tabs[activeTab];

    return (
        <Box id="demo" sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {/* Glow */}
            <Box sx={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', width: '70vw', height: '40vh', background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        PRODUCT DEMO
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.04em', mt: 1, mb: 2 }}>
                        See Socialixer in action.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 520, mx: 'auto', lineHeight: 1.8 }}>
                        A single workspace that replaces five different tools. No tabs, no context-switching, no missed leads.
                    </Typography>
                </Box>

                {/* Tab Bar — scrollable on mobile with reactive left + right arrows */}
                <Box sx={{
                    position: 'relative',
                    mb: { xs: 3, md: 6 },
                    '@keyframes nudgeRight': {
                        '0%':   { transform: 'translateX(0)',   opacity: 0.7 },
                        '50%':  { transform: 'translateX(6px)', opacity: 1   },
                        '100%': { transform: 'translateX(0)',   opacity: 0.7 },
                    },
                    '@keyframes nudgeLeft': {
                        '0%':   { transform: 'translateX(0)',    opacity: 0.7 },
                        '50%':  { transform: 'translateX(-6px)', opacity: 1   },
                        '100%': { transform: 'translateX(0)',    opacity: 0.7 },
                    },
                }}>
                    {/* Scrollable pill row */}
                    <Box
                        ref={tabScrollRef}
                        onScroll={handleTabScroll}
                        sx={{
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', md: 'center' },
                            gap: { xs: 1, md: 1.5 },
                            flexWrap: { xs: 'nowrap', md: 'wrap' },
                            overflowX: { xs: 'auto', md: 'visible' },
                            pb: { xs: 0.5, md: 0 },
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                        }}
                    >
                        {tabs.map((tab, i) => (
                            <Box
                                key={i}
                                onClick={() => setActiveTab(i)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.8,
                                    px: { xs: 2, md: 3 }, py: { xs: 1, md: 1.2 },
                                    borderRadius: '100px', cursor: 'pointer',
                                    flexShrink: 0,
                                    border: activeTab === i ? `1.5px solid ${tab.accent}60` : '1.5px solid rgba(255,255,255,0.1)',
                                    background: activeTab === i ? `linear-gradient(145deg, ${tab.accent}15 0%, ${tab.accent}08 100%)` : 'transparent',
                                    backdropFilter: activeTab === i ? 'blur(12px)' : 'none',
                                    boxShadow: activeTab === i ? `0 4px 16px -4px ${tab.accent}30` : 'none',
                                    transition: 'all 0.25s',
                                    '&:hover': { borderColor: `${tab.accent}50`, background: `${tab.accent}08` },
                                }}
                            >
                                <Box sx={{ color: activeTab === i ? tab.accent : 'rgba(255,255,255,0.5)', display: 'flex' }}>{tab.icon}</Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: activeTab === i ? tab.accent : 'rgba(255,255,255,0.6)', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                                    {tab.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* LEFT scroll hint — mobile only, appears once user scrolls right */}
                    <Box
                        onClick={scrollTabsLeft}
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            position: 'absolute',
                            top: 0, left: 0, bottom: 4,
                            width: 72,
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pl: 1,
                            background: 'linear-gradient(to left, transparent 0%, rgba(6,9,20,0.88) 55%, rgba(6,9,20,0.98) 100%)',
                            cursor: 'pointer',
                            transition: 'opacity 0.35s ease',
                            opacity: showLeftHint ? 1 : 0,
                            pointerEvents: showLeftHint ? 'auto' : 'none',
                            borderRadius: '100px 0 0 100px',
                            zIndex: 2,
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28, height: 28,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.10)',
                            border: '1px solid rgba(255,255,255,0.18)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            animation: showLeftHint ? 'nudgeLeft 1.3s ease-in-out infinite' : 'none',
                        }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', transform: 'rotate(180deg)' }} />
                        </Box>
                    </Box>

                    {/* RIGHT scroll hint — mobile only, hides at end */}
                    <Box
                        onClick={scrollTabsRight}
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            position: 'absolute',
                            top: 0, right: 0, bottom: 4,
                            width: 72,
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            pr: 1,
                            background: 'linear-gradient(to right, transparent 0%, rgba(6,9,20,0.88) 55%, rgba(6,9,20,0.98) 100%)',
                            cursor: 'pointer',
                            transition: 'opacity 0.35s ease',
                            opacity: showRightHint ? 1 : 0,
                            pointerEvents: showRightHint ? 'auto' : 'none',
                            borderRadius: '0 100px 100px 0',
                            zIndex: 2,
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28, height: 28,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.10)',
                            border: '1px solid rgba(255,255,255,0.18)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            animation: showRightHint ? 'nudgeRight 1.3s ease-in-out infinite' : 'none',
                        }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }} />
                        </Box>
                    </Box>
                </Box>

                {/* Demo Window */}
                <Box sx={{
                    borderRadius: { xs: '12px', md: '14px' },
                    border: `1.5px solid ${t.accent}20`,
                    background: `linear-gradient(145deg, ${t.accent}06 0%, rgba(15,23,42,0.85) 100%)`,
                    backdropFilter: 'blur(16px)',
                    overflow: 'hidden',
                    boxShadow: `0 40px 100px rgba(0,0,0,0.5), 0 0 60px -20px ${t.accent}15`,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: { xs: 'auto', md: 480 } }}>
                        {/* Sidebar — desktop only */}
                        <Box sx={{
                            display: { xs: 'none', md: 'block' },
                            width: 220,
                            borderRight: '1px solid rgba(255,255,255,0.06)',
                            p: 2,
                            bgcolor: 'rgba(255,255,255,0.01)',
                            flexShrink: 0,
                        }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700, letterSpacing: '0.12em', fontSize: '0.6rem', display: 'block', mb: 1.5 }}>WORKSPACE</Typography>
                            {[
                                { label: 'Dashboard', icon: <DashboardIcon sx={{ fontSize: 15 }} />, active: false },
                                { label: 'Walkthrough', icon: <PlayIcon sx={{ fontSize: 15 }} />, active: activeTab === 0 },
                                { label: 'Lead CRM', icon: <GroupIcon sx={{ fontSize: 15 }} />, active: activeTab === 1 },
                                { label: 'AI Content', icon: <AutoIcon sx={{ fontSize: 15 }} />, active: activeTab === 2 },
                                { label: 'Analytics', icon: <BarChartIcon sx={{ fontSize: 15 }} />, active: activeTab === 3 },
                                { label: 'Settings', icon: <TuneIcon sx={{ fontSize: 15 }} />, active: false },
                            ].map((item) => (
                                <Box key={item.label} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 1.5, py: 1, borderRadius: 2, mb: 0.5,
                                    bgcolor: item.active ? `${t.accent}15` : 'transparent',
                                    border: item.active ? `1px solid ${t.accent}30` : '1px solid transparent',
                                }}>
                                    <Box sx={{ color: item.active ? t.accent : 'rgba(255,255,255,0.35)' }}>{item.icon}</Box>
                                    <Typography variant="caption" sx={{ fontWeight: item.active ? 700 : 500, color: item.active ? t.accent : 'rgba(255,255,255,0.45)', fontSize: '0.78rem' }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Main content area */}
                        <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, minWidth: 0 }}>
                            {/* Active tab header — mobile shows compact version */}
                            <Box sx={{ mb: { xs: 1.5, md: 3 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: '8px',
                                    bgcolor: `${t.accent}15`, border: `1px solid ${t.accent}30`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: t.accent, flexShrink: 0,
                                }}>{t.icon}</Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.25rem' }, lineHeight: 1.2 }}>{t.label}</Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, fontSize: { xs: '0.75rem', md: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>{t.desc}</Typography>
                                </Box>
                            </Box>
                            {/* Mobile description */}
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, fontSize: '0.78rem', mb: 1.5, display: { xs: 'block', sm: 'none' } }}>{t.desc}</Typography>
                            <Box sx={{
                                borderRadius: { xs: 2, md: 3 }, overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.07)',
                                bgcolor: 'rgba(255,255,255,0.01)',
                                height: { xs: 240, sm: 300, md: 400 },
                            }}>
                                {activeTab === 1 ? (
                                    <DemoCrmPreview />
                                ) : (
                                    <Box component="img" src={t.image} alt={t.label}
                                        sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', objectPosition: 'top center' }}
                                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = `linear-gradient(135deg, ${t.accent}08 0%, transparent 100%)`; }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 5 }} />
            </Container>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   UNIFIED PLATFORM SECTION
   "One hub. All your platforms."
───────────────────────────────────────────────────────────────────────────── */
const UnifiedPlatformSection = () => {
    const platforms = [
        { name: 'Instagram', icon: <InstagramIcon />, color: '#e1306c', handle: '@yourbrand' },
        { name: 'YouTube', icon: <YouTubeIcon />, color: '#ff0000', handle: 'YourChannel' },
        { name: 'Facebook', icon: <FacebookIcon />, color: '#1877f2', handle: 'Your Page' },
    ];

    return (
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            {/* Background accent */}
            <Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(0,210,255,0.05) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    gap: { xs: 8, md: 12 },
                }}>
                    {/* Text side */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.8, borderRadius: '100px', bgcolor: 'rgba(0,210,255,0.07)', border: '1px solid rgba(0,210,255,0.2)', mb: 3 }}>
                            <DashboardIcon sx={{ fontSize: 14, color: '#00d2ff' }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                ALL-IN-ONE HUB
                            </Typography>
                        </Box>

                        <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.1, letterSpacing: '-0.04em', mb: 2.5 }}>
                            One platform. {' '}
                            <Box component="span" sx={{ background: 'linear-gradient(90deg, #00d2ff, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                All 3 platforms.
                            </Box>
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: '1.05rem', mb: 4, maxWidth: 460 }}>
                            Stop switching between apps. Post to YouTube, Instagram, and Facebook simultaneously. Connect your accounts and control everything from a single powerful dashboard.
                        </Typography>

                        <Stack spacing={1.5} sx={{ mb: 4 }}>
                            {[
                                'Connect unlimited accounts per platform',
                                'Unified inbox for comments, DMs & leads',
                                'Cross-platform analytics in one view',
                                'Publish & schedule across all channels',
                            ].map((pt, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CheckIcon sx={{ color: '#00d2ff', fontSize: 18, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{pt}</Typography>
                                </Box>
                            ))}
                        </Stack>

                    </Box>

                    {/* Visual: Platform cards */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{
                            p: 3, borderRadius: '14px',
                            background: 'linear-gradient(145deg, rgba(0,210,255,0.05) 0%, rgba(15,23,42,0.8) 100%)',
                            border: '1.5px solid rgba(0,210,255,0.15)',
                            backdropFilter: 'blur(16px)',
                            boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px -20px rgba(0,210,255,0.1)',
                            position: 'relative', overflow: 'hidden',
                        }}>
                            {/* Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 800, color: 'rgba(255,255,255,0.7)' }}>Connected Platforms</Typography>
                                <Chip label="3 Platforms" size="small" sx={{ bgcolor: 'rgba(0,210,255,0.1)', color: '#00d2ff', fontWeight: 700, border: '1px solid rgba(0,210,255,0.3)', fontSize: '0.65rem' }} />
                            </Box>

                            <Stack spacing={1.5}>
                                {platforms.map((p) => (
                                    <Box key={p.name} sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        p: 2, borderRadius: 3, position: 'relative', overflow: 'hidden',
                                        background: `linear-gradient(145deg, ${p.color}06 0%, rgba(15,23,42,0.5) 100%)`,
                                        border: `1px solid ${p.color}18`,
                                        backdropFilter: 'blur(8px)',
                                        transition: 'all 0.25s',
                                        '&:hover': { borderColor: `${p.color}40`, transform: 'translateX(4px)', boxShadow: `0 8px 24px -6px ${p.color}20` },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ color: p.color, display: 'flex', width: 32, height: 32, borderRadius: 2, bgcolor: `${p.color}15`, border: `1px solid ${p.color}30`, alignItems: 'center', justifyContent: 'center' }}>
                                                {p.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{p.name}</Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>{p.handle}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#28c940' }} />
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>Live</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            <Box sx={{ mt: 3, p: 2, borderRadius: 3, bgcolor: 'rgba(0,210,255,0.05)', border: '1px solid rgba(0,210,255,0.15)', display: 'flex', alignItems: 'center', gap: 2 }}>
                                <BoltIcon sx={{ color: '#00d2ff', fontSize: 18 }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                                    AI is actively monitoring <Box component="span" sx={{ color: '#00d2ff' }}>247 comments</Box> across all platforms
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   AI SEO / CAPTION SECTION
   "AI-generated titles, descriptions & hashtags"
───────────────────────────────────────────────────────────────────────────── */
const AiSeoSection = () => {
    const [activeExample, setActiveExample] = useState(0);

    const examples = [
        {
            platform: 'YouTube',
            input: 'My product tutorial video',
            title: 'How I 10x\'d My Sales Using This One AI Tool (Full Tutorial)',
            description: 'In this video, I break down the exact AI automation system that helped me convert Instagram comments into paying customers — step by step. No fluff.',
            tags: ['#AITool', '#SocialMediaGrowth', '#Automation', '#ContentCreator'],
            color: '#ff0000',
        },
        {
            platform: 'Instagram',
            input: 'Fitness transformation post',
            title: '6-Week Transformation ✨',
            description: 'The results only come when you stop waiting for motivation and start building discipline. Here\'s what 6 weeks of consistency looks like 📈 Comment "GUIDE" to get my free plan.',
            tags: ['#Transformation', '#FitnessJourney', '#Discipline', '#HealthyLifestyle'],
            color: '#e1306c',
        },
        {
            platform: 'Facebook',
            input: 'Quarterly business growth update',
            title: 'We grew 340% in Q4. Here\'s what changed.',
            description: 'A year ago, we were manually replying to every social comment. Today, AI handles 90% of it. Here are the 3 systems we implemented that completely changed our growth trajectory.',
            tags: ['#BusinessGrowth', '#AIStrategy', '#Entrepreneurship', '#B2B'],
            color: '#1877f2',
        },
    ];

    const ex = examples[activeExample];

    return (
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Box sx={{ position: 'absolute', top: '30%', left: '-5%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 8, md: 12 } }}>

                    {/* Live preview card */}
                    <Box sx={{ flex: 1, minWidth: 0, order: { xs: 2, md: 1 } }}>
                        {/* Platform switcher */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            {examples.map((e, i) => (
                                <Box key={i} onClick={() => setActiveExample(i)}
                                    sx={{
                                        px: 2.5, py: 0.8, borderRadius: '100px', cursor: 'pointer',
                                        border: activeExample === i ? `1px solid ${e.color}60` : '1px solid rgba(255,255,255,0.1)',
                                        bgcolor: activeExample === i ? `${e.color}12` : 'transparent',
                                        transition: 'all 0.2s',
                                    }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: activeExample === i ? e.color : 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                                        {e.platform}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ p: 3, borderRadius: '14px', background: `linear-gradient(145deg, ${ex.color}06 0%, rgba(15,23,42,0.8) 100%)`, border: `1.5px solid ${ex.color}20`, backdropFilter: 'blur(16px)', boxShadow: `0 30px 80px rgba(0,0,0,0.4), 0 0 50px -20px ${ex.color}15`, transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden' }}>
                            {/* Input */}
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', mb: 3 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.65rem', letterSpacing: '0.1em' }}>YOUR INPUT</Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>{ex.input}</Typography>
                            </Box>

                            {/* Generated title */}
                            <Box sx={{ mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TitleIcon sx={{ color: '#8b5cf6', fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.1em' }}>AI TITLE</Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.4, color: 'white' }}>{ex.title}</Typography>
                            </Box>

                            {/* Generated description */}
                            <Box sx={{ mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <AutoIcon sx={{ color: '#8b5cf6', fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.1em' }}>AI DESCRIPTION</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.7 }}>{ex.description}</Typography>
                            </Box>

                            {/* Tags */}
                            <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontWeight: 600, display: 'block', mb: 1, fontSize: '0.65rem', letterSpacing: '0.1em' }}>HASHTAGS / TAGS</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {ex.tags.map((tag) => (
                                        <Chip key={tag} label={tag} size="small" sx={{ bgcolor: `${ex.color}12`, color: ex.color, border: `1px solid ${ex.color}30`, fontWeight: 600, fontSize: '0.7rem' }} />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Text side */}
                    <Box sx={{ flex: 1, minWidth: 0, order: { xs: 1, md: 2 } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.8, borderRadius: '100px', bgcolor: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)', mb: 3 }}>
                            <AutoIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                AI SEO WRITER
                            </Typography>
                        </Box>

                        <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.1, letterSpacing: '-0.04em', mb: 2.5 }}>
                            AI writes your titles &{' '}
                            <Box component="span" sx={{ background: 'linear-gradient(90deg, #8b5cf6, #c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                descriptions.
                            </Box>
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: '1.05rem', mb: 4, maxWidth: 460 }}>
                            Stop spending hours crafting captions and video titles. Our Gemini-powered AI reads your content, understands your audience, and generates platform-optimised titles, descriptions, and hashtags — in seconds.
                        </Typography>

                        <Stack spacing={1.5} sx={{ mb: 4 }}>
                            {[
                                'Platform-specific tone & format',
                                'SEO-optimised for search & discovery',
                                'Hook-first writing that drives clicks',
                                'Hashtag & keyword recommendations',
                            ].map((pt, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CheckIcon sx={{ color: '#8b5cf6', fontSize: 18, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{pt}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Button component={Link} to="/app" endIcon={<ArrowForwardIcon />}
                            sx={{ color: '#8b5cf6', fontWeight: 800, fontSize: '1rem', textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.8 } }}>
                            Try AI writing now
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN LANDING PAGE
───────────────────────────────────────────────────────────────────────────── */
const LandingPage = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [waitlistMode, setWaitlistMode] = useState(false);

    useEffect(() => {
        featuresApi.getWaitlistMode().then(setWaitlistMode);
    }, []);

    // SALES ORDER: Revenue promise → Data/intelligence → Automation reinforcement
    const features = [
        {
            tag: 'AI AUTOMATION',
            tagColor: '#00d2ff',
            icon: <ReplyIcon sx={{ color: '#00d2ff', fontSize: 18 }} />,
            title: 'Turn every comment into a sales conversion.',
            description: 'Our sentiment-aware AI monitors your posts 24/7, detecting high-intent leads and replying instantly with your unique brand voice — while you sleep.',
            points: ['Real-time high-intent lead detection', 'Brand-synced AI reply personality', 'Automated sales funnel tracking'],
            imageSrc: '/assets/crm_preview.png',
            reverse: false, // text LEFT, image RIGHT
        },
        {
            tag: 'CONTENT INTELLIGENCE',
            tagColor: '#8b5cf6',
            icon: <InsightsIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
            title: 'Stop guessing. Start dominating the algorithm.',
            description: "Identify exactly what content your audience wants before you create it. Analyse competitor gaps and let AI tell you what to post, when to post, and how to hook them.",
            points: ['Content Gap Analysis vs competitors', 'Trend & keyword opportunity finder', 'AI-ranked post ideas by potential reach'],
            imageSrc: '/assets/hero_mockup.png',
            reverse: true, // image LEFT, text RIGHT
        },
        {
            tag: 'SMART ENGAGEMENT',
            tagColor: '#10d98a',
            icon: <AutoIcon sx={{ color: '#10d98a', fontSize: 18 }} />,
            title: 'Drive loyal traffic on autopilot.',
            description: 'Transform passive followers into active buyers. Our AI filters noise, prioritises high-value interactions, and keeps your audience engaged 24/7 — no team required.',
            points: ['Sentiment-based comment filtering', 'Dynamic CTA injection at the right moment', 'Full lead attribution & conversion tracking'],
            imageSrc: '/assets/crm_preview.png',
            reverse: false, // text LEFT, image RIGHT
        },
    ];

    return (
        <Box sx={{ bgcolor: '#05060f', minHeight: '100vh', color: 'white', fontFamily: '"Outfit", "Inter", sans-serif', overflowX: 'hidden' }}>

            {/* ── BACKGROUND ── */}
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: `radial-gradient(ellipse 80vw 60vw at 10% 0%, rgba(0,210,255,0.06) 0%, transparent 60%), radial-gradient(ellipse 60vw 60vw at 90% 80%, rgba(139,92,246,0.05) 0%, transparent 60%)` }} />
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.35, backgroundImage: `linear-gradient(rgba(0,210,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,210,255,0.04) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />

            {/* ── NAVIGATION ── */}
            <Box component="nav" sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, backdropFilter: 'blur(20px)', bgcolor: 'rgba(5,6,15,0.85)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {/* Main nav row */}
                <Box sx={{ py: 2, px: { xs: 2.5, md: 8 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1.1rem', color: '#000', boxShadow: '0 0 16px rgba(0,210,255,0.4)' }}>S</Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>Socialixer</Typography>
                    </Box>

                    {/* Desktop links */}
                    <Stack direction="row" spacing={4} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <MuiLink href="#features" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>Features</MuiLink>
                        <MuiLink href="#demo" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>Demo</MuiLink>
                        {!waitlistMode && <MuiLink href="#pricing" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>Pricing</MuiLink>}
                        <MuiLink href="#faq" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>FAQ</MuiLink>
                        <MuiLink component={Link} to="/blog" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>Blog</MuiLink>
                        {/* Log In button hidden — use /secret-login */}
                        <Button href={waitlistMode ? '#waitlist' : undefined} component={waitlistMode ? 'a' : Link} to={waitlistMode ? undefined : '/app'} variant="contained" sx={{ bgcolor: '#fff', color: '#000', fontWeight: 800, px: 3.5, borderRadius: '100px', '&:hover': { bgcolor: '#00d2ff', transform: 'translateY(-2px)' }, transition: 'all 0.25s' }}>
                            {waitlistMode ? 'Join Waitlist' : 'Get Started'}
                        </Button>
                    </Stack>

                    {/* Mobile hamburger */}
                    <IconButton
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        sx={{ display: { xs: 'flex', md: 'none' }, color: 'white', p: 1 }}
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>
                </Box>

                {/* Mobile dropdown */}
                <Collapse in={mobileMenuOpen}>
                    <Box sx={{ px: 2.5, pb: 3, display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                        {[['Features', '#features'], ['Demo', '#demo'], ...(!waitlistMode ? [['Pricing', '#pricing']] : []), ['FAQ', '#faq']].map(([label, href]) => (
                            <MuiLink
                                key={label}
                                href={href}
                                onClick={() => setMobileMenuOpen(false)}
                                sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, textDecoration: 'none', py: 1.5, fontSize: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}
                            >
                                {label}
                            </MuiLink>
                        ))}
                        <MuiLink
                            component={Link}
                            to="/blog"
                            onClick={() => setMobileMenuOpen(false)}
                            sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, textDecoration: 'none', py: 1.5, fontSize: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}
                        >
                            Blog
                        </MuiLink>
                        {/* Log In button hidden — use /secret-login */}
                        <Button href={waitlistMode ? '#waitlist' : undefined} component={waitlistMode ? 'a' : Link} to={waitlistMode ? undefined : '/app'} onClick={() => setMobileMenuOpen(false)} variant="contained" fullWidth sx={{ mt: 1, bgcolor: '#00d2ff', color: '#000', fontWeight: 800, py: 1.5, borderRadius: '100px', '&:hover': { bgcolor: '#fff' } }}>
                            {waitlistMode ? 'Join Waitlist' : 'Get Started'}
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* ── HERO ── */}
            <Box component="main" role="main">
                <Box component="header">
                    <Box sx={{ pt: { xs: 18, md: 22 }, pb: { xs: 10, md: 14 }, position: 'relative', zIndex: 1 }}>
                        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 6, xl: 10 } }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 8, md: 8 } }}>
                                {/* Text */}
                                <Box sx={{ flex: '0 0 auto', width: { md: '42%' }, minWidth: 0 }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 0.8, borderRadius: '100px', mb: 3, bgcolor: 'rgba(0,210,255,0.07)', border: '1px solid rgba(0,210,255,0.2)' }}>
                                        <BoltIcon sx={{ color: '#00d2ff', fontSize: 16 }} />
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.12em', textTransform: 'uppercase' }}>AI-Powered Social Growth</Typography>
                                    </Box>
                                    <Typography variant="h1" sx={{ fontSize: { xs: '2.8rem', md: '4.8rem' }, fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.04em', mb: 3, background: 'linear-gradient(160deg, #fff 50%, rgba(255,255,255,0.45) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                        Master Social Chaos{' '}
                                        <Box component="span" sx={{ color: '#00d2ff', WebkitTextFillColor: '#00d2ff' }}>with AI.</Box>
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.15rem' }, mb: 4, maxWidth: 480 }}>
                                        The primary AI layer for high-intent social growth. Post to YouTube, Instagram, and Facebook simultaneously, automate engagement, generate captions, and capture revenue — all from one platform.
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                                        {waitlistMode ? (
                                            <Button href="#waitlist" variant="contained" size="large"
                                                sx={{ py: 2, px: 5, fontSize: '1rem', fontWeight: 800, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 30px rgba(0,210,255,0.35)', '&:hover': { bgcolor: '#fff', transform: 'translateY(-3px)' }, transition: 'all 0.3s' }}>
                                                Join the Waitlist
                                            </Button>
                                        ) : (
                                            <Button component={Link} to="/app" variant="contained" size="large"
                                                sx={{ py: 2, px: 5, fontSize: '1rem', fontWeight: 800, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 30px rgba(0,210,255,0.35)', '&:hover': { bgcolor: '#fff', transform: 'translateY(-3px)' }, transition: 'all 0.3s' }}>
                                                Start for Free
                                            </Button>
                                        )}
                                        <Button href="#demo" variant="outlined" size="large" startIcon={<PlayIcon />}
                                            sx={{ py: 2, px: 5, fontSize: '1rem', fontWeight: 700, borderColor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '100px', '&:hover': { borderColor: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                            See Demo
                                        </Button>
                                    </Stack>
                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ gap: 2 }}>
                                        {[
                                            { icon: '🔒', text: 'No credit card required' },
                                            { icon: '⚡', text: 'Setup in 5 minutes' },
                                            { icon: '🤖', text: 'Powered by Gemini AI' },
                                        ].map((badge) => (
                                            <Box key={badge.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                <Typography sx={{ fontSize: '0.85rem' }}>{badge.icon}</Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {badge.text}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                {/* Hero image — dashboard screenshot carousel */}
                                <Box sx={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'visible' }}>
                                    <HeroDashboardCarousel />
                                </Box>
                            </Box>
                        </Container>
                    </Box>
                </Box>{/* /header */}

                {/* ── STATS ── */}
                <Box sx={{ py: 6, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'relative', zIndex: 1 }}>
                    <Container maxWidth="lg">
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                            gap: 2,
                        }}>
                            {[
                                { label: 'Core Platforms', value: '3', color: '#00d2ff' },
                                { label: 'Posting Mode', value: 'Simultaneous', color: '#8b5cf6' },
                                { label: 'Feature Cycle', value: 'Monthly', color: '#10d98a' },
                                { label: 'AI Support', value: '24/7', color: '#f59e0b' },
                            ].map((s, i) => (
                                <Box key={i} sx={{
                                    textAlign: 'center', py: 3, px: 2,
                                    borderRadius: '14px', position: 'relative', overflow: 'hidden',
                                    border: `1px solid ${s.color}20`,
                                    background: `linear-gradient(145deg, ${s.color}08 0%, rgba(15,23,42,0.6) 100%)`,
                                    backdropFilter: 'blur(16px)',
                                    transition: 'all 0.25s ease',
                                    '&:hover': { transform: 'translateY(-2px)', borderColor: `${s.color}40`, boxShadow: `0 12px 30px -8px ${s.color}25` },
                                }}>
                                    <Box sx={{ position: 'absolute', top: -30, right: -30, width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle, ${s.color}15 0%, transparent 70%)`, pointerEvents: 'none' }} />
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: s.color, letterSpacing: '-0.03em', lineHeight: 1, position: 'relative', fontSize: { xs: '1.75rem', sm: '1.25rem', md: '1.75rem', lg: '2rem' } }}>{s.value}</Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 600, mt: 0.5, position: 'relative' }}>{s.label}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </Box>

                {/* ── MONTHLY FEATURES CALLOUT ── */}
                <Box sx={{ py: 3, bgcolor: 'rgba(0,210,255,0.03)', borderBottom: '1px solid rgba(0,210,255,0.1)' }}>
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <AutoIcon sx={{ color: '#00d2ff', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.05em' }}>
                                NEW FEATURES LAUNCHING EVERY MONTH — STAY TUNED
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                {/* ── UNIFIED PLATFORM — Hook #1: Biggest pain point ── */}
                <UnifiedPlatformSection />

                {/* ── AI SEO WRITER — Hook #2: Immediate tangible win ── */}
                <AiSeoSection />

                {/* ── ZIGZAG FEATURES — Deep value props ── */}
                <Box id="features" sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}>
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
                            <Typography variant="overline" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.18em', fontSize: '0.7rem' }}>FEATURES</Typography>
                            <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.04em', mt: 1 }}>
                                Everything you need to dominate.
                            </Typography>
                        </Box>
                        {features.map((f, idx) => <ZigzagSection key={idx} {...f} />)}
                    </Container>
                </Box>

                {/* ── DEMO — Show before final CTA ── */}
                <DemoSection />


                {/* ── PRICING SECTION — hidden in waitlist mode ── */}
                {!waitlistMode && <PricingSection />}

                {/* ── FROM THE BLOG — latest 3 published posts ── */}
                <BlogSection />

                {/* ── WAITLIST / NEWSLETTER ── */}
                <Box id="waitlist">
                    <NewsletterSection />
                </Box>

                {/* ── FAQ SECTION (GEO signal — mirrors JSON-LD FAQPage schema) ── */}
                <FaqSection />

                {/* ── BOTTOM CTA ── */}
                <Box sx={{ py: { xs: 8, md: 12 }, textAlign: 'center', position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '60vw', height: '30vh', background: 'radial-gradient(circle, rgba(0,210,255,0.15) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />
                    <Container maxWidth="md" sx={{ position: 'relative' }}>
                        <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '4.5rem' }, letterSpacing: '-0.04em', lineHeight: 1.05, mb: 3 }}>
                            Take command of your future.
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.1rem', mb: 5, maxWidth: 500, mx: 'auto' }}>
                            Stop the manual grind. Start scaling your brand with AI that works as hard as you do.
                        </Typography>
                        {waitlistMode ? (
                            <Button href="#waitlist" variant="contained" size="large"
                                sx={{ py: 2.5, px: 10, fontSize: '1.2rem', fontWeight: 900, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 60px rgba(0,210,255,0.4)', '&:hover': { bgcolor: '#fff', transform: 'scale(1.05)' }, transition: 'all 0.3s' }}>
                                Join the Waitlist
                            </Button>
                        ) : (
                            <Button component={Link} to="/app" variant="contained" size="large"
                                sx={{ py: 2.5, px: 10, fontSize: '1.2rem', fontWeight: 900, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 60px rgba(0,210,255,0.4)', '&:hover': { bgcolor: '#fff', transform: 'scale(1.05)' }, transition: 'all 0.3s' }}>
                                Get Started Now
                            </Button>
                        )}
                    </Container>
                </Box>
            </Box>{/* /main */}

            {/* ── FOOTER ── */}
            <Box component="footer" sx={{ py: 10, borderTop: '1px solid rgba(255,255,255,0.05)', position: 'relative', zIndex: 1 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 6, mb: 6 }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#000' }}>S</Box>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>Socialixer</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', maxWidth: 280, lineHeight: 1.7 }}>
                                Building the infrastructure for the next generation of social creators.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: { xs: 6, md: 10 } }}>
                            {[
                                { heading: 'Platform', links: ['AI Growth', 'Lead CRM', 'Automation', 'AI Writer'] },
                                { heading: 'Legal', links: [['Terms', '/terms'], ['Privacy', '/privacy'], ['Refund', '/refund'], ['Cancellation', '/cancellation']] },
                            ].map((col) => (
                                <Stack key={col.heading} spacing={2}>
                                    <Typography variant="overline" sx={{ fontWeight: 900, color: 'white', letterSpacing: '0.12em', fontSize: '0.65rem' }}>{col.heading}</Typography>
                                    {col.links.map((l) =>
                                        Array.isArray(l) ? (
                                            <MuiLink key={l[0]} component={Link} to={l[1]} sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>{l[0]}</MuiLink>
                                        ) : (
                                            <MuiLink key={l} sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9rem', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>{l}</MuiLink>
                                        )
                                    )}
                                </Stack>
                            ))}
                        </Box>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 4 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
                        © {new Date().getFullYear()} Socialixer. All rights reserved.
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage;

/* ─────────────────────────────────────────────────────────────────────────────
   FROM THE BLOG SECTION
   Shows latest 3 published blog posts — links to Flask-rendered /blog/<slug>
───────────────────────────────────────────────────────────────────────────── */
function BlogSection() {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        blogApi.latest().then(res => {
            setPosts(Array.isArray(res) ? res : res.posts || []);
        }).catch(() => {});
    }, []);

    if (posts.length === 0) return null;

    return (
        <Box
            id="blog"
            component="section"
            sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
            {/* Dual ambient glow */}
            <Box sx={{ position: 'absolute', top: '30%', left: '20%', width: '40vw', height: '40vh', background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)', filter: 'blur(100px)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: '20%', right: '15%', width: '35vw', height: '35vh', background: 'radial-gradient(circle, rgba(0,210,255,0.06) 0%, transparent 70%)', filter: 'blur(100px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        FROM THE BLOG
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.04em', mt: 1, mb: 2, background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.7) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        Tips, guides & insights.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 520, mx: 'auto', lineHeight: 1.8 }}>
                        Learn how to grow your social media presence with AI-powered strategies.
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 2.5, md: 3 } }}>
                    {posts.map((post, idx) => (
                        <Box
                            key={post.slug}
                            component="a"
                            href={`/blog/${post.slug}`}
                            sx={{
                                display: 'flex', flexDirection: 'column', borderRadius: '20px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                background: 'rgba(255,255,255,0.03)',
                                backdropFilter: 'blur(20px)',
                                WebkitBackdropFilter: 'blur(20px)',
                                overflow: 'hidden', textDecoration: 'none', color: 'inherit',
                                transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
                                position: 'relative',
                                '&::before': {
                                    content: '""', position: 'absolute', inset: 0, borderRadius: '20px',
                                    background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, transparent 50%)',
                                    pointerEvents: 'none', zIndex: 1,
                                },
                                '&:hover': {
                                    border: '1px solid rgba(0,210,255,0.25)',
                                    transform: 'translateY(-6px)',
                                    boxShadow: '0 25px 50px rgba(0,0,0,0.35), 0 0 40px rgba(0,210,255,0.08)',
                                    background: 'rgba(255,255,255,0.06)',
                                },
                                '&:hover img': { transform: 'scale(1.05)' },
                            }}
                        >
                            {post.cover_image ? (
                                <Box sx={{ aspectRatio: '16/9', overflow: 'hidden', position: 'relative' }}>
                                    <Box component="img" src={post.cover_image} alt={post.title} loading="lazy"
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1)' }} />
                                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(to top, rgba(2,6,23,0.7), transparent)', pointerEvents: 'none' }} />
                                </Box>
                            ) : (
                                <Box sx={{
                                    aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    position: 'relative', overflow: 'hidden',
                                    background: idx === 0
                                        ? 'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(0,210,255,0.15) 100%)'
                                        : idx === 1
                                        ? 'linear-gradient(135deg, rgba(0,210,255,0.18) 0%, rgba(34,197,94,0.12) 100%)'
                                        : 'linear-gradient(135deg, rgba(244,114,182,0.15) 0%, rgba(99,102,241,0.18) 100%)',
                                }}>
                                    <Box sx={{ position: 'absolute', width: '120px', height: '120px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)', top: '20%', right: '-10%' }} />
                                    <Box sx={{ position: 'absolute', width: '80px', height: '80px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)', bottom: '15%', left: '-5%' }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.15em', zIndex: 1 }}>
                                        {(post.category || 'article').replace('-', ' ')}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ p: { xs: 2, md: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
                                {post.category && (
                                    <Chip label={post.category.replace(/-/g, ' ')} size="small"
                                        sx={{
                                            alignSelf: 'flex-start', mb: 1.5,
                                            bgcolor: 'rgba(0,210,255,0.08)', color: '#00d2ff',
                                            fontWeight: 700, fontSize: '0.6rem', textTransform: 'uppercase',
                                            letterSpacing: '0.08em', height: 22,
                                            border: '1px solid rgba(0,210,255,0.15)',
                                            backdropFilter: 'blur(8px)',
                                        }} />
                                )}
                                <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.08rem' }, lineHeight: 1.45, mb: 1.5, color: 'white', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {post.title}
                                </Typography>
                                {post.meta_description && (
                                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.82rem', lineHeight: 1.6, mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
                                        {post.meta_description}
                                    </Typography>
                                )}
                                <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', gap: 1.5, color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                                        <span>{post.reading_time_min || 1} min read</span>
                                    </Box>
                                    <Typography sx={{ color: '#00d2ff', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.02em', opacity: 0.7, transition: 'opacity 0.2s', '.MuiBox-root:hover &': { opacity: 1 } }}>
                                        Read more &rarr;
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
                    <Button component="a" href="/blog" variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)',
                            borderRadius: '100px', px: 4, py: 1.5, fontWeight: 700,
                            backdropFilter: 'blur(12px)',
                            background: 'rgba(255,255,255,0.03)',
                            '&:hover': { borderColor: 'rgba(0,210,255,0.4)', color: '#00d2ff', bgcolor: 'rgba(0,210,255,0.06)', boxShadow: '0 0 20px rgba(0,210,255,0.1)' },
                        }}
                    >
                        View All Articles
                    </Button>
                </Box>
            </Container>
        </Box>
    );
}

/* ─────────────────────────────────────────────────────────────────────────────
   FAQ SECTION
   Mirrors the JSON-LD FAQPage schema in index.html.
   Visible Q&A text is the primary GEO signal — AI engines parse it the same
   way they parse web content, so entity-rich, direct answers here help
   Socialixer get cited in AI-generated answers (ChatGPT, Perplexity, SGE).
───────────────────────────────────────────────────────────────────────────── */
function FaqSection() {
    const [open, setOpen] = useState(null);
    const [nerdMode, setNerdMode] = useState(false);
    const [detailOpen, setDetailOpen] = useState({});

    const toggle = (i) => {
        setOpen(open === i ? null : i);
        if (open === i) setDetailOpen(prev => ({ ...prev, [i]: false }));
    };

    const toggleDetail = (i, e) => {
        e.stopPropagation();
        setDetailOpen(prev => ({ ...prev, [i]: !prev[i] }));
    };

    const faqs = [
        {
            q: "Is this gonna charge me at 3am? \uD83D\uDCB8",
            tldr: "Nope. Free. No card. No sneaky charges. Ever.",
            humanA: "It's free. Like, actually free. No credit card at signup, no trial period, no 'gotcha' moment. You just bring your own API keys from the platforms you use (most have free tiers anyway). Socialixer itself costs you nothing.",
            nerdA: "Socialixer is free and self-hostable \u2014 no credit card required. You bring your own API credentials for each social platform (Instagram, Twitter, LinkedIn, etc.) and your own AI API key (Google Gemini is free-tier eligible). There is no per-seat or per-post cost from Socialixer itself.",
            badge: "\uD83D\uDD25 237 asked",
            mostAsked: true,
        },
        {
            q: "You're not selling my passwords right?? \uD83D\uDD10",
            tldr: "Your creds are encrypted so hard even WE can't read them. Pinky swear. \uD83E\uDD19",
            humanA: "Your API keys are encrypted before they're saved \u2014 think Fort Knox but for your passwords. The encryption key lives only on the server, never in the database, never in your browser. In the UI, your credentials show as \u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 so even you can't accidentally flash them on a screen share.",
            nerdA: "Yes. All API credentials are encrypted with Fernet (AES-128-CBC + HMAC-SHA256 authentication) before being written to Firebase Firestore. The Fernet encryption key lives exclusively in the server-side environment variable CREDENTIAL_ENCRYPTION_KEY and is never stored in the database or sent to the browser. Credentials are decrypted server-side on demand and values are masked (shown as \u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022) in the frontend Settings UI.",
            badge: "\uD83D\uDC80 148 asked",
        },
        {
            q: "Wait... what even IS Socialixer? \uD83D\uDC40",
            tldr: "Your AI hype-man for social media. Posts for you. Thinks for you. No burnout.",
            humanA: "Socialixer is like having a social media manager in your pocket \u2014 but make it AI. It schedules your posts, generates banger content ideas, and handles DMs and comments on autopilot. Works on YouTube, Instagram, and Facebook. One dashboard, zero chaos.",
            nerdA: "Socialixer is an AI-powered social media automation platform. It lets creators and brands schedule posts, generate AI content ideas (powered by Google Gemini), and manage social media leads \u2014 all from a single dashboard. It currently supports YouTube, Instagram, and Facebook with simultaneous posting, with more platforms coming soon.",
        },
        {
            q: "Will it reply to my comments automatically? \uD83E\uDD16",
            tldr: "Yes \u2014 smart AI replies, only when it's safe to. No spammy nonsense.",
            humanA: "When someone comments on your Instagram post, Socialixer catches it in real-time, figures out if it's a lead, a question, or just noise, and if auto-reply is on, it fires back with a relevant reply. It has safety filters so it won't reply to spam or weird stuff. And it remembers what it already replied to so nothing gets double-replied.",
            nerdA: "Socialixer uses Instagram's Webhooks API to receive real-time notifications whenever someone comments on your posts. An AI intent classifier categorises each comment as a lead, question, spam, positive, or negative signal. If auto-reply is enabled and the comment passes all safety filters, Socialixer generates a contextually relevant reply and posts it via the Graph API. Processed comment IDs are persisted in Firestore, so duplicate replies are prevented even across server restarts.",
        },
        {
            q: "How does the AI content magic actually work? \u2728",
            tldr: "You type a topic. Gemini AI cooks. You post. That's it.",
            humanA: "Drop in a topic (like 'Monday motivation for gym rats'), pick your platform, and Socialixer asks Google Gemini to whip up content ideas complete with hooks, captions, hashtags, and even a format suggestion (Reel? Carousel? Thread?). It even predicts how well each idea will perform. If the AI goes offline, backup templates keep things running.",
            nerdA: "Enter a topic and optionally select a target platform. Socialixer sends this to Google Gemini 1.5 Flash, which returns structured ideas: each idea includes a title, hook (opening line), full post body, call-to-action, recommended format (Short Post / Thread / Story / Reel / Carousel / Poll), engagement prediction (High / Medium / Low), and relevant hashtags. If AI is unavailable, a heuristic template library provides instant fallback ideas.",
        },
        {
            q: "Which apps does it work with tho? \uD83D\uDCF2",
            tldr: "YouTube, Instagram, Facebook \u2014 and more coming soon bestie.",
            humanA: "Right now: YouTube, Instagram, and Facebook. You can post to all three at the same time from one place. Twitter/X and LinkedIn are on the roadmap so hang tight.",
            nerdA: "Socialixer currently focuses on three core platforms: YouTube (video uploads via YouTube Data API v3), Instagram (feed posts and Reels via Graph API), and Facebook (page posts via Graph API). We are expanding to support Twitter/X, LinkedIn, and others in future monthly updates.",
        },
        {
            q: "Content gap analysis?? ok explain \uD83D\uDC47",
            tldr: "It spots what your competitor is doing better and tells you how to close the gap.",
            humanA: "Paste your post and a competitor's post (or URL). Socialixer compares them side by side \u2014 keywords, tone, engagement signals, readability \u2014 scores the difference, and then has AI rewrite your post 5 different ways to outperform theirs. It's like a cheat code for content.",
            nerdA: "Content gap analysis compares your social media post against a competitor's post or URL. Socialixer analyses both for keyword frequency, topic coverage, sentiment, readability (Flesch reading ease), and engagement signals (questions, emojis, hashtags, CTAs, numbers). It scores the gap and uses Gemini AI to suggest 5 rewritten versions of your post that close the identified gaps.",
        },
    ];

    return (
        <Box
            id="faq"
            component="section"
            aria-labelledby="faq-heading"
            sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
            <GlobalStyles styles={`
                @keyframes faqBounceIn {
                    0%   { opacity: 0; transform: translateY(-8px) scale(0.98); }
                    60%  { opacity: 1; transform: translateY(2px) scale(1.005); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `} />

            {/* Glow */}
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '60vw', height: '40vh', background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="md" sx={{ position: 'relative' }}>
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        GOT QUESTIONS? WE GOT YOU
                    </Typography>
                    <Typography
                        id="faq-heading"
                        variant="h2"
                        sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.04em', mt: 1, mb: 2 }}
                    >
                        no dumb questions here {'\uD83D\uDC9C'}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 480, mx: 'auto', lineHeight: 1.8, mb: 4 }}>
                        real talk about how Socialixer works.
                    </Typography>

                    {/* Human / Nerd Mode Toggle */}
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center',
                        bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '999px',
                        p: '4px', gap: '4px', border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                        <Box
                            component="button"
                            onClick={() => setNerdMode(false)}
                            sx={{
                                cursor: 'pointer', border: 'none', outline: 'none',
                                px: 2.5, py: 0.75, borderRadius: '999px',
                                fontWeight: 700, fontSize: '0.85rem',
                                bgcolor: !nerdMode ? '#8b5cf6' : 'transparent',
                                color: !nerdMode ? '#fff' : 'rgba(255,255,255,0.5)',
                                transition: 'all 0.2s',
                            }}
                        >
                            {'\u2728'} Human
                        </Box>
                        <Box
                            component="button"
                            onClick={() => setNerdMode(true)}
                            sx={{
                                cursor: 'pointer', border: 'none', outline: 'none',
                                px: 2.5, py: 0.75, borderRadius: '999px',
                                fontWeight: 700, fontSize: '0.85rem',
                                bgcolor: nerdMode ? '#8b5cf6' : 'transparent',
                                color: nerdMode ? '#fff' : 'rgba(255,255,255,0.5)',
                                transition: 'all 0.2s',
                            }}
                        >
                            {'\uD83E\uDD13'} Nerd
                        </Box>
                    </Box>
                </Box>

                <Box component="dl" sx={{ m: 0, p: 0 }}>
                    {faqs.map((faq, i) => (
                        <Box
                            key={i}
                            sx={{
                                mb: 2,
                                borderRadius: '14px',
                                border: open === i ? '1.5px solid rgba(139,92,246,0.35)' : '1.5px solid rgba(255,255,255,0.07)',
                                background: open === i
                                    ? 'linear-gradient(145deg, rgba(139,92,246,0.08) 0%, rgba(15,23,42,0.8) 100%)'
                                    : 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(15,23,42,0.6) 100%)',
                                backdropFilter: 'blur(16px)',
                                transition: 'all 0.25s',
                                overflow: 'hidden',
                                '&:hover': { borderColor: 'rgba(139,92,246,0.3)', transform: 'translateY(-1px)', boxShadow: '0 8px 24px -6px rgba(139,92,246,0.15)' },
                            }}
                        >
                            {/* Question */}
                            <Box
                                component="dt"
                                role="button"
                                tabIndex={0}
                                aria-expanded={open === i}
                                onClick={() => toggle(i)}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(i)}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    px: 3, py: 2.5, cursor: 'pointer', gap: 2, m: 0,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.5 }}
                                    >
                                        {faq.q}
                                    </Typography>
                                    {faq.badge && (
                                        <Chip
                                            label={faq.badge}
                                            size="small"
                                            sx={{
                                                fontSize: '0.65rem',
                                                height: 20,
                                                bgcolor: 'rgba(139,92,246,0.2)',
                                                color: '#c4b5fd',
                                                border: '1px solid rgba(139,92,246,0.35)',
                                                fontWeight: 700,
                                                '& .MuiChip-label': { px: 1 },
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ color: open === i ? '#8b5cf6' : 'rgba(255,255,255,0.4)', flexShrink: 0, display: 'flex', transition: 'color 0.2s' }}>
                                    {open === i ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Box>
                            </Box>

                            {/* Answer */}
                            <Collapse in={open === i}>
                                <Box
                                    component="dd"
                                    sx={{
                                        px: 3, pb: 3, pt: 0, m: 0,
                                        animation: open === i ? 'faqBounceIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
                                    }}
                                >
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2.5 }} />

                                    {/* TL;DR Box */}
                                    <Box sx={{
                                        bgcolor: 'rgba(139,92,246,0.1)',
                                        borderLeft: '3px solid #8b5cf6',
                                        borderRadius: '0 8px 8px 0',
                                        px: 2, py: 1.5, mb: 1.5,
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Chip
                                                label="TL;DR"
                                                size="small"
                                                sx={{
                                                    fontSize: '0.6rem',
                                                    height: 18,
                                                    bgcolor: '#8b5cf6',
                                                    color: '#fff',
                                                    fontWeight: 800,
                                                    flexShrink: 0,
                                                    mt: '2px',
                                                    '& .MuiChip-label': { px: 0.75 },
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontSize: '0.9rem', fontWeight: 500 }}
                                            >
                                                {faq.tldr}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* "okay but actually →" expand toggle */}
                                    <Box
                                        component="span"
                                        onClick={(e) => toggleDetail(i, e)}
                                        sx={{
                                            display: 'inline-block',
                                            cursor: 'pointer',
                                            color: '#a78bfa',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            mb: detailOpen[i] ? 1.5 : 0,
                                            userSelect: 'none',
                                            '&:hover': { color: '#c4b5fd', textDecoration: 'underline' },
                                            transition: 'color 0.15s',
                                        }}
                                    >
                                        {detailOpen[i] ? 'okay I get it \u2191' : 'okay but actually \u2192'}
                                    </Box>

                                    {/* Full answer — max-height CSS transition */}
                                    <Box
                                        sx={{
                                            maxHeight: detailOpen[i] ? '600px' : '0px',
                                            overflow: 'hidden',
                                            transition: 'max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.85, fontSize: '0.95rem', pt: 0.5 }}
                                        >
                                            {nerdMode ? faq.nerdA : faq.humanA}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    ))}
                </Box>

            </Container>
        </Box>
    );
}
